import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authService, MfaResponse } from '../services/auth';
import { api } from '../services/api';

interface User {
  id: string;
  email: string;
  name: string;
  profile?: string;
  organizationId?: string[] | string;
  nameOrganization?: string;
}

// Primeiro, vamos verificar a resposta real da API
type ApiMfaResponse = {
  access_token: string;
  userId: string;
  organizationId: string[];
  profile: string;
}

interface AuthContextData {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresMfa: boolean;
  tempEmail: string | null;
  signIn: (email: string, password: string) => Promise<boolean>;
  verifyMfa: (email: string, code: string) => Promise<boolean>;
  signOut: () => void;
  hasAdminAccess: () => boolean;
  isManager: () => boolean;
  getUserOrganizationId: () => string | null;
  updateUserOrganizationName: (nameOrganization: string) => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    // Verifica se já existe um usuário no localStorage
    const storedUser = localStorage.getItem('@App:user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [tempEmail, setTempEmail] = useState<string | null>(null);

  // Log para debug - verificar dados do usuário carregados
  useEffect(() => {
    console.log("AuthContext - Usuário atual:", user);
    if (user) {
      console.log("AuthContext - Nome da organização:", user.nameOrganization);
      console.log("AuthContext - Dados do localStorage:", localStorage.getItem('@App:user'));
    }
  }, [user]);

  const signIn = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await authService.login(email, password);
      
      // Verificar se o usuário precisa de MFA baseado na resposta do backend
      if (response.requireMFA === true && response.sendMFACode) {
        // MFA é obrigatório, mostrar tela de código
        setRequiresMfa(true);
        setTempEmail(response.email);
        return true; // Login inicial bem-sucedido, aguardando MFA
      } else if (response.requireMFA === false && response.access_token) {
        // MFA não é obrigatório, fazer login direto
        console.log("Usuário não precisa de MFA, fazendo login direto");
        console.log("Dados recebidos do backend:", response);
        
        // Processar login direto (similar ao que acontece no verifyMfa)
        const orgName = response.nameOrganization || 
                       response.organizationName || 
                       response.organization_name || 
                       response.orgName || 
                       "Organização";
        
        // Extrair role do JWT token para determinar o profile
        let userProfile = 'user';
        try {
          if (response.access_token) {
            const tokenPayload = JSON.parse(atob(response.access_token.split('.')[1]));
            console.log("Token payload:", tokenPayload);
            userProfile = tokenPayload.role || response.profile || 'user';
          }
        } catch (error) {
          console.error("Erro ao decodificar token:", error);
          userProfile = response.profile || 'user';
        }
        
        const loggedUser = {
          id: response.userId!,
          email: email,
          name: email.split('@')[0], // Extrair nome do email
          organizationId: response.organizationId!,
          profile: userProfile,
          nameOrganization: orgName
        };

        console.log("Salvando usuário:", loggedUser);

        // Salvar dados do usuário
        localStorage.setItem('@App:token', response.access_token);
        localStorage.setItem('@App:user', JSON.stringify(loggedUser));
        setUser(loggedUser);
        setRequiresMfa(false);
        setTempEmail(null);
        
        console.log("Login direto concluído com sucesso");
        console.log("Estado setUser:", loggedUser);
        console.log("requiresMfa definido como:", false);
        console.log("Dados salvos no localStorage:", {
          token: localStorage.getItem('@App:token'),
          user: JSON.parse(localStorage.getItem('@App:user') || '{}')
        });
        
        if (response.organizationId && response.access_token && (!orgName || orgName === "Organização")) {
          const orgId = Array.isArray(response.organizationId) ? response.organizationId[0] : response.organizationId;
          if (orgId) {
            try {
              const orgResponse = await api.get(`/api/organization/${orgId}`, {
                headers: {
                  'Authorization': `Bearer ${response.access_token}`
                }
              });

              if (orgResponse.data?.nameOrganization) {
                const updatedUser = {
                  ...loggedUser,
                  nameOrganization: orgResponse.data.nameOrganization
                };

                localStorage.setItem('@App:user', JSON.stringify(updatedUser));
                setUser(updatedUser);
                console.log("Nome da organização atualizado após login:", orgResponse.data.nameOrganization);
              }
            } catch (orgError) {
              console.error("Erro ao buscar dados da organização após login:", orgError);
            }
          }
        }

        return true; // Login completo e bem-sucedido
      }

      // Se chegou aqui, algo deu errado
      return false;

    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyMfa = useCallback(async (email: string, code: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await authService.verifyMfa(email, code);

      if (response && response.access_token) {
        const user = {
          id: response.userId,
          email: email,
          name: email.split('@')[0],
          organizationId: response.organizationId,
          profile: response.profile,
          nameOrganization: response.nameOrganization
        };

        // Log para debug
        console.log("Dados do usuário sendo salvos no localStorage:", user);
        console.log("Nome da organização:", response.nameOrganization);

        // Primeiro limpar os estados antigos
        setRequiresMfa(false);
        setTempEmail(null);

        // Salvar dados iniciais
        localStorage.setItem('@App:token', response.access_token);
        localStorage.setItem('@App:user', JSON.stringify(user));
        setUser(user);

        // Buscar nome real da organização se necessário
        if (response.organizationId && response.organizationId.length > 0 && 
            (!response.nameOrganization || response.nameOrganization === "Organização")) {
          try {
            const orgId = Array.isArray(response.organizationId) ? response.organizationId[0] : response.organizationId;
            console.log("Buscando dados da organização com ID:", orgId);
           // alert(`Buscando organização com ID: ${orgId}`);
            
            const orgResponse = await api.get(`/api/organization/${orgId}`, {
              headers: {
                'Authorization': `Bearer ${response.access_token}`
              }
            });

            if (orgResponse.data && orgResponse.data.nameOrganization) {
              console.log("Nome real da organização encontrado:", orgResponse.data.nameOrganization);
             // alert(`Nome da organização encontrado: ${orgResponse.data.nameOrganization}`);
              
              // Atualizar usuário com nome real da organização
              const updatedUser = {
                ...user,
                nameOrganization: orgResponse.data.nameOrganization
              };
              
              localStorage.setItem('@App:user', JSON.stringify(updatedUser));
              setUser(updatedUser);
            } else {
             // alert("API da organização não retornou nameOrganization");
              console.log("Resposta da API de organização:", orgResponse.data);
            }
          } catch (orgError) {
            console.error("Erro ao buscar dados da organização:", orgError);
            alert(`Erro ao buscar organização: ${orgError instanceof Error ? orgError.message : 'Erro desconhecido'}`);
          }
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro na verificação MFA:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@App:token');
    localStorage.removeItem('@App:user');
    setUser(null);
    setRequiresMfa(false);
  }, []);

  // Função para verificar se o usuário tem acesso de administrador
  const hasAdminAccess = useCallback(() => {
    if (!user || !user.profile) {
      console.log("hasAdminAccess: false - usuário ou perfil não encontrado");
      return false;
    }
    const isAdmin = user.profile.toLowerCase() === 'admin';
    console.log(`hasAdminAccess: ${isAdmin} - perfil: ${user.profile}`);
    return isAdmin;
  }, [user]);

  // Função para verificar se o usuário é um manager
  const isManager = useCallback(() => {
    if (!user || !user.profile) {
      console.log("isManager: false - usuário ou perfil não encontrado");
      return false;
    }
    const isManagerUser = user.profile.toLowerCase() === 'manager';
    console.log(`isManager: ${isManagerUser} - perfil: ${user.profile}`);
    return isManagerUser;
  }, [user]);

  // Função para obter o ID da organização do usuário atual
  const getUserOrganizationId = useCallback(() => {
    if (!user || !user.organizationId) {
      console.log("[AuthContext] Não foi possível obter organizationId do usuário:", user);
      return null;
    }
    
    // Se for um array, retorna o primeiro item; caso contrário, retorna o valor como está
    const id = Array.isArray(user.organizationId) 
      ? user.organizationId[0] 
      : user.organizationId;
    
    console.log("[AuthContext] ID da organização recuperado:", id);
    return id;
  }, [user]);

  // Função para atualizar o nome da organização no contexto e no localStorage
  const updateUserOrganizationName = useCallback((nameOrganization: string) => {
    if (!user) return;
    
    // Verifica se o valor já é o mesmo para evitar atualizações desnecessárias
    if (user.nameOrganization === nameOrganization) {
      console.log("[AuthContext] Nome da organização já está atualizado:", nameOrganization);
      return;
    }
    
    console.log("[AuthContext] Atualizando nome da organização para:", nameOrganization);
    
    // Atualiza no contexto
    setUser(prevUser => {
      if (!prevUser) return null;
      return {
        ...prevUser,
        nameOrganization
      };
    });
    
    // Atualiza no localStorage
    const storedUserData = localStorage.getItem('@App:user');
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        
        // Evita atualização desnecessária se o valor for o mesmo
        if (userData.nameOrganization === nameOrganization) {
          return;
        }
        
        userData.nameOrganization = nameOrganization;
        localStorage.setItem('@App:user', JSON.stringify(userData));
      } catch (error) {
        console.error('[AuthContext] Erro ao atualizar nome da organização no localStorage:', error);
      }
    }
  }, [user]);

  return (
    <AuthContext.Provider 
      value={{ 
        user,
        isAuthenticated: !!user,
        isLoading,
        requiresMfa,
        tempEmail,
        signIn,
        verifyMfa,
        signOut,
        hasAdminAccess,
        isManager,
        getUserOrganizationId,
        updateUserOrganizationName
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};